// French (vous) copy for /preview-first-uploads, translated from copy.en.ts. Same shape (checked by copy.test.ts).
// The second pass is always "les originaux" (never "pleine qualité"). The uploader's own labels are in English,
// so they're quoted as it shows them. French typography: a non-breaking space before : ; ? ! and inside « ».
import type { PreviewFirstCopy } from "./copy.en";

const fr: PreviewFirstCopy = {
  seo: {
    title: "Aperçus d'abord, sur toutes les offres | Photohawk",
    description:
      "Nouveau dans l'outil d'envoi web de Photohawk, sur toutes les offres : un petit aperçu de chaque photo part en premier, pour que votre galerie puisse être en ligne, vendre et être partagée en quelques minutes pendant que les originaux suivent en arrière-plan.",
  },
  hero: {
    badge: "Nouveau",
    eyebrow: "Dans l'outil d'envoi web · Toutes les offres",
    title: "Votre galerie en ligne en quelques minutes,",
    titleHighlight: "pas en quelques heures",
    subtitle:
      "L'outil d'envoi web envoie désormais d'abord un petit aperçu de chaque photo. Votre galerie peut être mise en ligne avec ces aperçus\u00a0: les acheteurs trouvent, achètent et partagent leurs photos pendant que les originaux continuent de s'envoyer en arrière-plan.",
    ctaPrimary: "Ouvrir l'outil d'envoi",
    ctaSecondary: "Voir comment ça marche",
    uploaderLink: "Voir toutes les nouveautés de l'outil d'envoi web",
  },
  steps: {
    heading: "Comment ça marche",
    subtitle: "Les aperçus d'abord sont dans l'outil d'envoi web, sur toutes les offres. Quand vous ajoutez des photos, il indique combien de temps prend chaque option, et vous choisissez « Previews first, originals later ».",
    stepLabel: "Étape {n}",
    items: [
      {
        id: "add",
        title: "Ajoutez vos photos",
        body: "Déposez des photos ou un dossier entier, comme d'habitude. Votre navigateur crée un petit aperçu de chacune, directement sur votre ordinateur.",
      },
      {
        id: "previews",
        title: "Les aperçus partent en premier",
        body: "Les aperçus sont légers, donc ils s'envoient vite, même avec une connexion lente sur place.",
      },
      {
        id: "live",
        title: "Votre galerie est en ligne",
        body: "Publiez-la dès que les aperçus sont envoyés, ou cochez «\u00a0Publish the gallery as soon as the previews are up\u00a0» pour que cela se fasse tout seul. Les acheteurs peuvent parcourir, rechercher, acheter et partager tout de suite.",
      },
      {
        id: "full",
        title: "Les originaux suivent",
        body: "Les originaux continuent de s'envoyer en arrière-plan, dans les mêmes photos. Les téléchargements et les tirages les utilisent toujours.",
      },
    ],
    timeline: {
      label: "Un exemple d'envoi, du début à la fin",
      previews: "Aperçus",
      previewsTime: "quelques minutes",
      live: "Galerie en ligne",
      full: "Originaux",
      fullTime: "en arrière-plan",
    },
  },
  buyers: {
    heading: "Ce que voient les acheteurs",
    subtitle: "Rien de nouveau à apprendre. Les acheteurs retrouvent la galerie qu'ils connaissent, simplement plus tôt.",
    items: [
      {
        id: "protected",
        title: "Filigranés et protégés, comme d'habitude",
        body: "Les acheteurs parcourent les mêmes aperçus filigranés que d'habitude, et la protection des aperçus fonctionne de la même façon si votre galerie l'utilise.",
      },
      {
        id: "search",
        title: "Chacun se retrouve tout de suite",
        body: "La recherche par selfie et par dossard fonctionne sur les aperçus\u00a0: chacun trouve ses photos dès que la galerie est en ligne.",
      },
      {
        id: "delivery",
        title: "Les originaux, livrés automatiquement",
        body: "Un achat ou un téléchargement gratuit n'est jamais livré sous forme d'aperçu. Si quelqu'un achète ou télécharge une photo avant l'arrivée de son original, celui-ci passe en tête de la file, et la personne le reçoit dès qu'il est prêt.",
      },
    ],
  },
  strip: {
    heading: "Gardez un œil sur l'envoi",
    body: "Pendant l'envoi des aperçus et des originaux, un bandeau en haut de l'outil d'envoi indique où en est votre galerie\u00a0: si elle est déjà en ligne, combien d'originaux il reste à envoyer et quand une photo achetée est passée en tête. Une fois la galerie en ligne, il indique aussi combien de personnes l'ont visitée au cours des 30 dernières minutes, et les propriétaires et administrateurs de l'espace de travail voient les nouvelles commandes depuis le début de l'envoi.",
    exampleLabel: "Exemple",
    live: "En ligne avec les aperçus",
    orders: "3 commandes depuis le début",
    visitors: "42 visiteurs ces 30 dernières min",
    left: "Encore 1\u00a0250 originaux à envoyer",
    jumped: "Les originaux de 2 photos commandées sont passés en tête",
  },
  faq: {
    heading: "Questions",
    items: [
      {
        q: "Dois-je garder l'outil d'envoi ouvert\u00a0?",
        a: "C'est préférable, jusqu'à ce que les originaux soient tous envoyés\u00a0; le bandeau indique combien il en reste. Si vous le fermez avant, rien n'est perdu. Votre galerie reste en ligne avec les aperçus et, la prochaine fois que vous ouvrez cette galerie dans l'outil d'envoi, il vous indique combien de photos attendent encore. Ajoutez à nouveau le même dossier (même plus tard, depuis chez vous)\u00a0: seuls les originaux manquants sont envoyés.",
      },
      {
        q: "Et si quelqu'un achète une photo avant l'arrivée de son original\u00a0?",
        a: "L'achat reste possible. Tant que l'outil d'envoi tourne, l'original de cette photo passe en tête de la file. L'e-mail de commande attend le fichier et part dans les minutes qui suivent son arrivée\u00a0: l'acheteur ne reçoit donc jamais l'aperçu par erreur. Si l'outil d'envoi a été fermé, la commande attend que vous ajoutiez à nouveau le dossier. L'outil d'envoi vous indique les photos qu'un acheteur attend, et celles-ci partent en premier.",
      },
      {
        q: "Est-ce que ça marche pour les galeries en téléchargement gratuit\u00a0?",
        a: "Oui. Chacun peut voir, rechercher et télécharger dès que les aperçus sont envoyés, et un téléchargement n'est jamais l'aperçu. Si quelqu'un télécharge une photo avant l'arrivée de son original, celui-ci passe en tête de la file\u00a0: le téléchargement indique que l'original est en cours d'envoi et devient un vrai téléchargement dès que le fichier arrive, et un téléchargement envoyé par e-mail part à ce moment-là. Chaque téléchargement ne compte qu'une fois.",
      },
      {
        q: "La recherche par selfie et par dossard fonctionne-t-elle sur les aperçus\u00a0?",
        a: "Oui. Les visages et les numéros de dossard sont détectés sur les aperçus, donc la recherche fonctionne dès qu'ils sont envoyés. Tant que l'outil d'envoi est ouvert, il envoie aussi des gros plans plus nets tirés de vos originaux pour aider la recherche.",
      },
      {
        q: "Quelles offres l'incluent\u00a0?",
        a: "Toutes les offres, Lite compris. Si votre espace de travail utilisait déjà les aperçus d'abord, ces photos se terminent normalement : ajoutez à nouveau le même dossier et leurs originaux sont envoyés, et leurs commandes et téléchargements arrivent toujours.",
      },
      {
        q: "Est-ce que ça coûte plus cher\u00a0?",
        a: "Non. C'est inclus dans toutes les offres, et chaque photo ne compte qu'une fois : l'aperçu et son original sont la même photo. La seule différence, c'est que votre connexion envoie un peu plus de données au total (environ 5 % de plus).",
      },
      {
        q: "Est-ce que ça marche avec le FTP et Photohawk Studio\u00a0?",
        a: "Pas pour le moment. Les aperçus d'abord font partie de l'outil d'envoi web. Les photos envoyées par FTP ou depuis Photohawk Studio partent directement en originaux, en une seule fois, comme avant.",
      },
      {
        q: "Est-ce activé par défaut ?",
        a: "Non. Chaque fois que vous ajoutez des photos, l'outil d'envoi web indique combien de temps elles prendront avec et sans aperçus d'abord, et l'envoi direct des originaux est sélectionné. Choisissez « Previews first, originals later » pour que la galerie soit en ligne plus tôt ; votre choix est mémorisé pour la prochaine fois. Avec une résolution d'envoi plus petite (« Large » ou « Web »), seuls des fichiers redimensionnés sont envoyés, donc les aperçus d'abord ne s'appliquent pas.",
      },
    ],
  },
  cta: {
    heading: "Essayez-le lors de votre prochain envoi",
    body: "Les aperçus d'abord sont dans l'outil d'envoi web, sur toutes les offres. Ajoutez vos photos, choisissez les aperçus d'abord : votre galerie peut être en ligne en quelques minutes.",
    button: "Ouvrir l'outil d'envoi",
  },
};

export default fr;
